import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { BarChart3, ChevronDown, Gauge, History, Inbox, Plug, Search, Settings as SettingsIcon, SlidersHorizontal, Users } from 'lucide-react'
import { cls } from '../lib/utils'
import { WorkspaceSkeleton } from './UI'
import { supabase } from '../lib/supabase'
import { TeamScopeProvider, useTeamScope } from './TeamScope'

const nav = [
  ['/app', 'Dashboard', Gauge],
  ['/app/queue', 'Live queue', Inbox],
  ['/app/team', 'Team', Users],
  ['/app/sla-performance', 'SLA Performance', SlidersHorizontal],
  ['/app/reports', 'Reports', BarChart3],
  ['/app/history', 'History', History],
  ['/app/integrations', 'Integrations', Plug],
  ['/app/settings', 'Settings', SettingsIcon],
] as const

const roleLabels: Record<string, string> = {
  owner: 'Workspace owner',
  admin: 'Workspace admin',
  manager: 'Manager',
  member: 'Team member',
  viewer: 'Viewer',
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'MP'
  return parts.slice(0, 2).map(part => part[0]?.toUpperCase() ?? '').join('')
}

function AppShellContent() {
  const location = useLocation()
  const { teams, selectedTeamId, selectedTeam, setSelectedTeamId, loadingTeams } = useTeamScope()

  const title = location.pathname.match(/^\/app\/team\/[^/]+\/records/)
    ? 'Employee records'
    : location.pathname.startsWith('/app/history/')
      ? 'History report'
      : location.pathname.startsWith('/app/team/')
        ? 'Employee'
        : (location.pathname.startsWith('/app/sla-performance/') || location.pathname.startsWith('/app/benchmarks/'))
          ? 'SLA Performance'
          : nav.find(([path]) => path === location.pathname)?.[1] ?? 'Mail Percent'

  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })

  const [displayName, setDisplayName] = useState('Account')
  const [roleLabel, setRoleLabel] = useState('Workspace member')
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)

  useEffect(() => {
    let active = true

    async function loadIdentity() {
      if (!supabase) return

      const { data } = await supabase.auth.getUser()
      const user = data.user
      if (!user || !active) return

      const storedName = String(user.user_metadata?.full_name ?? '').trim()
      const fallbackName = user.email?.split('@')[0] || 'Account'
      setDisplayName(storedName || fallbackName)

      const membership = await supabase
        .from('memberships')
        .select('role')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (!active || membership.error || !membership.data?.role) return
      setRoleLabel(roleLabels[membership.data.role] ?? 'Workspace member')
    }

    loadIdentity()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    setAccountMenuOpen(false)
  }, [location.pathname])

  const avatarText = useMemo(() => initials(displayName), [displayName])
  const scopeLabel = selectedTeam?.name ?? 'All teams'

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand">
        <img className="mail-percent-logo app-logo" src="/images/brand/mail-percent-logo.png" alt="Mail Percent" />
        <span className="app-brand-name">Mail Percent</span>
      </div>

      <div className="manager-team-switcher">
        <span className="manager-team-switcher-label">Teams</span>
        <div className="manager-team-switcher-control">
          <Users size={16}/>
          <select
            value={selectedTeamId ?? 'all'}
            onChange={event => setSelectedTeamId(event.target.value === 'all' ? null : event.target.value)}
            disabled={loadingTeams || teams.length === 0}
            aria-label="Choose manager team"
          >
            <option value="all">All teams</option>
            {teams.map(team => <option value={team.id} key={team.id}>{team.name}</option>)}
          </select>
          <ChevronDown size={14}/>
        </div>
      </div>

      <nav>
        {nav.map(([path,label,Icon]) => (
          <NavLink
            key={path}
            end={path === '/app'}
            to={path}
            className={({isActive}) => cls('nav-item', isActive && 'active')}
          >
            <Icon size={18}/>
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-bottom">
        <div className="profile sidebar-profile">
          <div className="avatar">{avatarText}</div>

          <div className="sidebar-profile-copy">
            <strong>{displayName}</strong>
            <span>{roleLabel}</span>
          </div>

          <div className="sidebar-account-menu-wrap">
            <button
              type="button"
              className="sidebar-account-trigger"
              aria-label="Open account options"
              aria-expanded={accountMenuOpen}
              onClick={() => setAccountMenuOpen(value => !value)}
            >
              •••
            </button>

            {accountMenuOpen && (
              <div className="sidebar-account-menu">
                <Link to="/">Back to public site</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>

    <main className="main">
      <header className="topbar">
        <div>
          <span className="eyebrow">{scopeLabel}</span>
          <h1>{title}</h1>
        </div>

        <div className="top-actions">
          <div className="search">
            <Search size={17}/>
            <input placeholder="Search mail, people, reports…"/>
          </div>
          <button className="date-chip">{dateLabel}</button>
        </div>
      </header>

      <div className="content">
        {loadingTeams ? <WorkspaceSkeleton rows={4}/> : <Outlet />}
      </div>
    </main>
  </div>
}

export function AppShell() {
  return <TeamScopeProvider><AppShellContent/></TeamScopeProvider>
}
